import React, { useMemo, useState } from 'react';

import { Box } from '@mui/material';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';

import SettaSwitch from '../../components/settaSwitch';
import {
    formatBackendDatePtBr,
    formatMonthShortPtBr,
    formatMonthYearPtBr,
    getMonthKey,
    parseBackendDate
} from '../../utils/dateUtils';
import { formatDecimal } from '../../utils/numberUtils';

const chartHeight = 240;

const getShortDateLabel = (value) => {
    const date = parseBackendDate(value);

    if (!date) {
        return value;
    }

    return String(date.getDate()).padStart(2, '0');
};

const getDailyTooltipLabel = (value) => formatBackendDatePtBr(value, value);

const groupRowsByMonth = (rows) => {
    const monthlyRows = new Map();

    rows.forEach((row) => {
        const monthKey = getMonthKey(row.data);

        if (!monthKey) {
            return;
        }

        const currentRow = monthlyRows.get(monthKey) ?? {
            data: `${monthKey}-01`,
            geracao: 0,
            perda: 0,
            estimado: 0
        };

        monthlyRows.set(monthKey, {
            data: currentRow.data,
            geracao: currentRow.geracao + row.geracao,
            perda: currentRow.perda + row.perda,
            estimado: currentRow.estimado + row.estimado
        });
    });

    return [...monthlyRows.entries()]
        .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
        .map(([, row]) => row);
};

const getColumnCenter = (index, total) => ((index + 0.5) / total) * 100;

const getPlotValue = (value) => (Number.isFinite(value) && value > 0 ? value : 0);

const getPlotValues = (row) => {
    const geracao = getPlotValue(row.geracao);
    const perda = getPlotValue(row.perda);

    return {
        geracao,
        perda,
        estimado: getPlotValue(row.estimado),
        total: geracao + perda
    };
};

const buildLinePath = (rows, maxValue) => {
    if (rows.length === 0 || maxValue === 0) {
        return '';
    }

    if (rows.length === 1) {
        const y = 100 - ((getPlotValue(rows[0].estimado) / maxValue) * 100);
        return `M 44 ${y} L 56 ${y}`;
    }

    return rows.map((row, index) => {
        const x = getColumnCenter(index, rows.length);
        const y = 100 - ((getPlotValue(row.estimado) / maxValue) * 100);
        return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
    }).join(' ');
};

const FalhasBissChart = ({ rows }) => {
    const [activeTooltip, setActiveTooltip] = useState(null);
    const [isMonthly, setIsMonthly] = useState(false);
    const chartRows = useMemo(
        () => (isMonthly ? groupRowsByMonth(rows) : rows),
        [isMonthly, rows]
    );
    const getPeriodLabel = isMonthly ? formatMonthShortPtBr : getShortDateLabel;
    const getTooltipLabel = isMonthly ? formatMonthYearPtBr : getDailyTooltipLabel;
    const maxValue = useMemo(() => Math.max(
        0,
        ...chartRows.map((row) => getPlotValues(row).total),
        ...chartRows.map((row) => getPlotValue(row.estimado))
    ), [chartRows]);
    const yTicks = useMemo(() => [1, 0.75, 0.5, 0.25, 0].map((ratio) => ({
        ratio,
        value: maxValue * ratio
    })), [maxValue]);
    const linePath = useMemo(() => buildLinePath(chartRows, maxValue), [chartRows, maxValue]);

    const handleGroupingChange = (checked) => {
        setActiveTooltip(null);
        setIsMonthly(checked);
    };

    return (
        <Box className='falhas-biss-chart-panel' onMouseLeave={() => setActiveTooltip(null)}>
            <Box className='falhas-biss-section-heading'>
                <span><InsightsRoundedIcon /> Geração, perda e estimado</span>
                <Box className='falhas-biss-chart-controls'>
                    <Box className='falhas-biss-chart-legend'>
                        <span className='is-generation'>Geração</span>
                        <span className='is-loss'>Perda</span>
                        <span className='is-estimated'>Estimado</span>
                    </Box>
                    <Box className='falhas-biss-chart-grouping'>
                        <span className={isMonthly ? '' : 'is-active'}>Diário</span>
                        <SettaSwitch
                            checked={isMonthly}
                            className='is-compact'
                            ariaLabel='Alternar entre agrupamento diário e mensal'
                            onChange={handleGroupingChange}
                        />
                        <span className={isMonthly ? 'is-active' : ''}>Mensal</span>
                    </Box>
                </Box>
            </Box>

            {activeTooltip && (
                <Box className='falhas-biss-chart-tooltip'>
                    <span>{getTooltipLabel(activeTooltip.data)}</span>
                    <strong>Geração: {formatDecimal(activeTooltip.geracao)} kWh</strong>
                    <strong>Perda: {formatDecimal(activeTooltip.perda)} kWh</strong>
                    <strong>Estimado: {formatDecimal(activeTooltip.estimado)} kWh</strong>
                </Box>
            )}

            <Box className='falhas-biss-column-chart' role='img' aria-label='Geração e perda empilhadas comparadas ao estimado'>
                <Box className='falhas-biss-y-axis'>
                    {yTicks.map((tick) => (
                        <span key={tick.ratio}>{formatDecimal(tick.value)}</span>
                    ))}
                </Box>
                <Box className='falhas-biss-plot' style={{ height: chartHeight }}>
                    {yTicks.map((tick) => (
                        <span
                            className='falhas-biss-grid-line'
                            key={tick.ratio}
                            style={{ bottom: `${tick.ratio * 100}%` }}
                        />
                    ))}

                    <svg className='falhas-biss-estimated-line' preserveAspectRatio='none' viewBox='0 0 100 100'>
                        <path d={linePath} />
                    </svg>

                    <Box className={`falhas-biss-periods${isMonthly ? ' is-monthly' : ''}`}>
                        {chartRows.map((row) => {
                            const plotValues = getPlotValues(row);
                            const totalHeight = maxValue === 0 ? 0 : (plotValues.total / maxValue) * 100;
                            const generationHeight = plotValues.total === 0 ? 0 : (plotValues.geracao / plotValues.total) * 100;
                            const lossHeight = plotValues.total === 0 ? 0 : (plotValues.perda / plotValues.total) * 100;

                            return (
                                <Box className='falhas-biss-period-group' key={row.data}>
                                    <Box className='falhas-biss-column-group'>
                                        <button
                                            type='button'
                                            className='falhas-biss-stacked-column'
                                            style={{ height: `${totalHeight}%` }}
                                            title={`${getTooltipLabel(row.data)}: ${formatDecimal(row.geracao)} kWh gerados, ${formatDecimal(row.perda)} kWh perdidos, ${formatDecimal(row.estimado)} kWh estimados`}
                                            onBlur={() => setActiveTooltip(null)}
                                            onFocus={() => setActiveTooltip(row)}
                                            onMouseEnter={() => setActiveTooltip(row)}
                                        >
                                            {plotValues.perda > 0 && (
                                                <span className='is-loss' style={{ height: `${lossHeight}%` }} />
                                            )}
                                            {plotValues.geracao > 0 && (
                                                <span className='is-generation' style={{ height: `${generationHeight}%` }} />
                                            )}
                                        </button>
                                    </Box>
                                    <span className='falhas-biss-period-label'>{getPeriodLabel(row.data)}</span>
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        </Box>
    );
};

export default FalhasBissChart;
